/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import AutosizeInput from 'react-input-autosize';
import { EuiScreenReaderOnly } from '../../accessibility';
import { EuiFormControlLayout } from '../../form/form_control_layout';
import { EuiComboBoxPill } from './combo_box_pill';
export class EuiComboBoxInput extends Component {
  state = {
    hasFocus: false
  };
  updatePosition = () => {
    // Wait a beat for the DOM to update, since we depend on DOM elements' bounds.
    requestAnimationFrame(() => {
      this.props.updatePosition();
    });
  };
  onFocus = event => {
    this.props.onFocus(event);
    this.setState({
      hasFocus: true
    });
  };
  onBlur = event => {
    if (this.props.onBlur) {
      this.props.onBlur(event);
    }

    this.setState({
      hasFocus: false
    });
  };

  componentDidUpdate(prevProps) {
    const {
      searchValue
    } = prevProps; // We need to update the position of everything if the user enters enough input to change
    // the size of the input.

    if (searchValue !== this.props.searchValue) {
      this.updatePosition();
    }
  }

  inputOnChange = event => {
    const {
      onChange,
      searchValue
    } = this.props;

    if (onChange) {
      onChange(event.target.value);
    }
  };
  inputRefCallback = ref => {
    const {
      autoSizeInputRef
    } = this.props;

    if (autoSizeInputRef) {
      autoSizeInputRef(ref);
    }
  };

  render() {
    const {
      compressed,
      focusedOptionId,
      fullWidth,
      hasSelectedOptions,
      id,
      inputRef,
      isDisabled,
      isListOpen,
      noIcon,
      onClear,
      onClick,
      onCloseListClick,
      onOpenListClick,
      onRemoveOption,
      placeholder,
      rootId,
      searchValue,
      selectedOptions,
      singleSelection: singleSelectionProp,
      toggleButtonRef,
      value,
      prepend,
      append,
      isLoading,
      autoFocus,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby
    } = this.props;
    const singleSelection = Boolean(singleSelectionProp);
    const asPlainText = singleSelectionProp && typeof singleSelectionProp === 'object' && singleSelectionProp.asPlainText || false;
    const pills = selectedOptions ? selectedOptions.map(option => {
      const {
        key,
        label,
        color,
        onClick,
        ...rest
      } = option;
      const pillOnClose = isDisabled || singleSelection || onClick ? undefined : onRemoveOption;
      return <EuiComboBoxPill option={option} onClose={pillOnClose} key={key ?? label.toLowerCase()} color={color} onClick={onClick} onClickAriaLabel={onClick ? 'Change' : undefined} asPlainText={asPlainText} {...rest}>
              {label}
            </EuiComboBoxPill>;
    }) : null;
    let removeOptionMessage;
    let removeOptionMessageId;

    if (this.state.hasFocus) {
      const readPlaceholder = placeholder ? `${placeholder}.` : '';
      const removeOptionMessageContent = `Combo box. Selected. ${searchValue ? `${searchValue}. Selected. ` : ''}${selectedOptions && selectedOptions.length > 0 ? `${value}. Press Backspace to delete ${selectedOptions[selectedOptions.length - 1].label}. ` : ''}Combo box input. ${readPlaceholder} Type some text or, to display a list of choices, press Down Arrow. ` + 'To exit the list of choices, press Escape.';
      removeOptionMessageId = rootId('removeOptionMessage'); // aria-live="assertive" will read this message aloud immediately once it enters the DOM.
      // We'll render to the DOM when the input gains focus and remove it when the input loses focus.
      // We'll use aria-hidden to prevent default aria information from being read by the screen
      // reader.

      removeOptionMessage = <EuiScreenReaderOnly>
          <span aria-live="polite" id={removeOptionMessageId}>
            {removeOptionMessageContent}
          </span>
        </EuiScreenReaderOnly>;
    }

    let placeholderMessage;

    if (placeholder && selectedOptions && !selectedOptions.length && !searchValue) {
      placeholderMessage = <p className="euiComboBoxPlaceholder">{placeholder}</p>;
    }

    const clickProps = {};

    if (!isDisabled && onClear && hasSelectedOptions) {
      clickProps.clear = {
        'data-test-subj': 'comboBoxClearButton',
        onClick: onClear
      };
    }

    let icon;

    if (!noIcon) {
      icon = {
        'aria-label': isListOpen ? 'Close list of options' : 'Open list of options',
        'data-test-subj': 'comboBoxToggleListButton',
        disabled: isDisabled,
        onClick: isListOpen && !isDisabled ? onCloseListClick : onOpenListClick,
        ref: toggleButtonRef,
        side: 'right',
        tabIndex: -1,
        type: 'arrowDown'
      };
    }

    const wrapClasses = classNames('euiComboBox__inputWrap', {
      'euiComboBox__inputWrap--compressed': compressed,
      'euiComboBox__inputWrap--fullWidth': fullWidth,
      'euiComboBox__inputWrap--noWrap': singleSelection,
      'euiComboBox__inputWrap-isLoading': isLoading,
      'euiComboBox__inputWrap-isClearable': onClear,
      'euiComboBox__inputWrap--inGroup': prepend || append
    });
    return <EuiFormControlLayout icon={icon} {...clickProps} inputId={id} isLoading={isLoading} compressed={compressed} fullWidth={fullWidth} prepend={prepend} append={append}>
        <div className={wrapClasses} data-test-subj="comboBoxInput" onClick={onClick} tabIndex={-1} // becomes onBlur event's relatedTarget, otherwise relatedTarget is null when clicking on this div
      >
          {!singleSelection || !searchValue ? pills : null}
          {placeholderMessage}
          <AutosizeInput aria-activedescendant={focusedOptionId} aria-autocomplete="list" aria-controls={isListOpen ? rootId('listbox') : ''} aria-expanded={isListOpen} aria-label={ariaLabel} aria-labelledby={ariaLabelledby} className="euiComboBox__input" data-test-subj="comboBoxSearchInput" disabled={isDisabled} id={id} inputRef={inputRef} onBlur={this.onBlur} onChange={this.inputOnChange} onFocus={this.onFocus} ref={this.inputRefCallback} role="combobox" style={{
          fontSize: 14
        }} value={searchValue} autoFocus={autoFocus} />
          {removeOptionMessage}
        </div>
      </EuiFormControlLayout>;
  }

}
EuiComboBoxInput.propTypes = {
  autoSizeInputRef: PropTypes.any,
  compressed: PropTypes.bool.isRequired,
  focusedOptionId: PropTypes.string,
  fullWidth: PropTypes.bool,
  hasSelectedOptions: PropTypes.bool.isRequired,
  id: PropTypes.string,
  inputRef: PropTypes.any,
  isDisabled: PropTypes.bool,
  isListOpen: PropTypes.bool.isRequired,
  noIcon: PropTypes.bool.isRequired,
  onBlur: PropTypes.any,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  onClick: PropTypes.func,
  onCloseListClick: PropTypes.func.isRequired,
  onFocus: PropTypes.any.isRequired,
  onOpenListClick: PropTypes.func.isRequired,
  onRemoveOption: PropTypes.func,
  placeholder: PropTypes.string,
  rootId: PropTypes.any.isRequired,
  searchValue: PropTypes.string.isRequired,
  selectedOptions: PropTypes.arrayOf(PropTypes.shape({
    isGroupLabelOption: PropTypes.bool,
    label: PropTypes.string.isRequired,
    key: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      isGroupLabelOption: PropTypes.bool,
      label: PropTypes.string.isRequired,
      key: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.any.isRequired),
      value: PropTypes.any,
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string
    }).isRequired),
    value: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string
  }).isRequired),
  singleSelection: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
    asPlainText: PropTypes.bool
  }).isRequired]),
  toggleButtonRef: PropTypes.any,
  updatePosition: PropTypes.func.isRequired,
  value: PropTypes.string,
  prepend: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired).isRequired]),
  append: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired).isRequired]),
  isLoading: PropTypes.bool,
  autoFocus: PropTypes.bool,
  "aria-label": PropTypes.string,
  "aria-labelledby": PropTypes.string,
  className: PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  EuiComboBoxInput.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
    description: '',
    displayName: 'EuiComboBoxInput',
    methods: [],
    props: {
      autoSizeInputRef: {
        defaultValue: null,
        description: '',
        name: 'autoSizeInputRef',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: '(instance: AutosizeInput & HTMLInputElement) => void'
        }
      },
      compressed: {
        defaultValue: null,
        description: '',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      focusedOptionId: {
        defaultValue: null,
        description: '',
        name: 'focusedOptionId',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      fullWidth: {
        defaultValue: null,
        description: '',
        name: 'fullWidth',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      hasSelectedOptions: {
        defaultValue: null,
        description: '',
        name: 'hasSelectedOptions',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      inputRef: {
        defaultValue: null,
        description: '',
        name: 'inputRef',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: '(instance: HTMLInputElement) => void'
        }
      },
      isDisabled: {
        defaultValue: null,
        description: '',
        name: 'isDisabled',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isListOpen: {
        defaultValue: null,
        description: '',
        name: 'isListOpen',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      noIcon: {
        defaultValue: null,
        description: '',
        name: 'noIcon',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'FocusEventHandler<HTMLInputElement>'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: '(searchValue: string) => void'
        }
      },
      onClear: {
        defaultValue: null,
        description: '',
        name: 'onClear',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      },
      onCloseListClick: {
        defaultValue: null,
        description: '',
        name: 'onCloseListClick',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: '() => void'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'FocusEventHandler<HTMLInputElement>'
        }
      },
      onOpenListClick: {
        defaultValue: null,
        description: '',
        name: 'onOpenListClick',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: '() => void'
        }
      },
      onRemoveOption: {
        defaultValue: null,
        description: '',
        name: 'onRemoveOption',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'OptionHandler<T>'
        }
      },
      placeholder: {
        defaultValue: null,
        description: '',
        name: 'placeholder',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      rootId: {
        defaultValue: null,
        description: '',
        name: 'rootId',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: '(idSuffix?: string) => string'
        }
      },
      searchValue: {
        defaultValue: null,
        description: '',
        name: 'searchValue',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      selectedOptions: {
        defaultValue: null,
        description: '',
        name: 'selectedOptions',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'EuiComboBoxOptionOption<T>[]'
        }
      },
      singleSelection: {
        defaultValue: null,
        description: '',
        name: 'singleSelection',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'boolean | EuiComboBoxSingleSelectionShape'
        }
      },
      toggleButtonRef: {
        defaultValue: null,
        description: '',
        name: 'toggleButtonRef',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: '(instance: HTMLButtonElement | HTMLSpanElement) => void'
        }
      },
      updatePosition: {
        defaultValue: null,
        description: '',
        name: 'updatePosition',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: true,
        type: {
          name: 'UpdatePositionHandler'
        }
      },
      value: {
        defaultValue: null,
        description: '',
        name: 'value',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      prepend: {
        defaultValue: null,
        description: '',
        name: 'prepend',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      append: {
        defaultValue: null,
        description: '',
        name: 'append',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      isLoading: {
        defaultValue: null,
        description: '',
        name: 'isLoading',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      autoFocus: {
        defaultValue: null,
        description: '',
        name: 'autoFocus',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-labelledby': {
        defaultValue: null,
        description: '',
        name: 'aria-labelledby',
        parent: {
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        },
        declarations: [{
          fileName: 'app/src/components/combo_box/combo_box_input/combo_box_input.tsx',
          name: 'EuiComboBoxInputProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['EuiComboBoxInputProps', 'CommonProps']
  };
} catch (e) {}